/**
 * Carrying what the old boards record to the platform.
 *
 * Renders nothing. It sits in the shell, because the boards' forms open from
 * dashboards and a shared modal rather than from one host, and registers itself
 * as the engine's record sink once the engine is here — never causing it to
 * load. When a board records something, the matching write (`writes.ts`) sends
 * it, the record is stamped with how the platform took it, the board redraws,
 * and the shell says so.
 *
 * It runs under both adapters. Under the mock the record goes to the demo's
 * own in-memory store and the board looks as it always did, plus "saved".
 *
 * Everything here catches. The engine's try/catch around the sink only sees a
 * synchronous throw; a rejected promise escaping from here would be an
 * unhandled-rejection crash line, and a record half-saved must never take the
 * board down with it.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession, type Session } from "../../app/SessionContext";
import { hasPermission } from "../../api/dataSource";
import { t } from "../../i18n";
import { demoIfLoaded, whenDemoLoaded } from "../domain/load";
import { requestRepaint } from "../lib/repaint";
import { writeFor, type RecordStamp } from "./writes";
import { sayWrite } from "./writeNoticeStore";
import "./writes/index";

type Stamped = { platform?: RecordStamp };

/** Redraw whatever board is showing. Only this: an answer can land after the person has left the board. */
function redraw(): void {
  try {
    demoIfLoaded()?.dashDraw();
  } catch {
    /* the dashboard board is not mounted; the others repaint below */
  }
  requestRepaint();
}

const newKey = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Send one record. Exported for the tests; the shell reaches it through the
 * engine's sink.
 */
export async function carry(
  session: Session,
  invalidate: (keys: readonly unknown[][]) => void,
  kind: string,
  rec: Stamped,
  extra: unknown,
): Promise<void> {
  const spec = writeFor(kind);
  if (!spec || !rec || typeof rec !== "object") return;
  const key = rec.platform?.key ?? newKey();
  rec.platform = { state: "saving", key };
  redraw();
  try {
    const need = spec.permission?.(rec, extra);
    if (need && !hasPermission(session.principal, need)) {
      const why = t("Not permitted: your role does not hold {p}.", { p: need });
      rec.platform = { state: "refused", key, why };
      sayWrite("warn", t("Not saved on the platform: {why}", { why }));
      return;
    }
    const ack = await spec.send({ source: session.source, siteId: session.site.id, principal: session.principal, key }, rec, extra);
    rec.platform = {
      state: "saved",
      key,
      id: ack.id,
      reference: ack.reference,
      ...(ack.at ? { at: ack.at } : {}),
      ...(ack.kind ? { kind: ack.kind } : {}),
    };
    invalidate(spec.invalidate(session.site.id) as unknown[][]);
    sayWrite(ack.tone ?? "ok", ack.text ?? t("{ref} saved on the platform.", { ref: ack.reference }));
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    rec.platform = { state: "refused", key, why };
    sayWrite(
      "warn",
      t("Not saved on the platform: {why}", { why }),
      spec.retryable ? () => void carry(session, invalidate, kind, rec, extra) : undefined,
    );
  } finally {
    redraw();
  }
}

export function LegacyWrites() {
  const session = useSession();
  const qc = useQueryClient();
  /* the session in a ref, so a site switch is never a stale closure */
  const ref = useRef(session);
  ref.current = session;

  useEffect(() => {
    let gone = false;
    const sink = (kind: string, rec: Stamped, extra: unknown) => {
      void carry(
        ref.current,
        (keys) => {
          for (const k of keys) void qc.invalidateQueries({ queryKey: k });
        },
        kind,
        rec,
        extra,
      ).catch(() => {
        /* carry catches its own; this is the belt to its braces */
      });
    };
    whenDemoLoaded((d) => {
      if (!gone) d.onRecord(sink);
    });
    return () => {
      gone = true;
      demoIfLoaded()?.onRecord(null);
    };
  }, [qc]);

  return null;
}
